import React, { useEffect, useState } from "react";
import Flex from "../../../components/Flex";
import { useHistory, useParams } from "react-router-dom";
import Content from "../../../components/Content";
import Button from "../../../components/Button";
import ButtonList from "../../../components/ButtonList";
import { useMessageToast } from "../../../components/MessageProvider";
import { useFetch } from "../../../components/useFetch";
import { useAuthState } from "../../../components/AuthProvider";
import { Formik } from "formik";
import * as Yup from "yup";
import Label from "../../../components/Label";
import TextBox from "../../../components/TextBox";
import Tooltip from "../../../components/Tooltip";
import Spinner from "../../../components/Loader/Spinner";

import styles from "./StartRerunTask.module.scss";

const DataExtractionTask = taskData => {
  const clientConfigApiDomain = process.env.REACT_APP_CLIENT_CONFIG_API_DOMAIN;
  const dataExtractionLoadsRpcUrl = `${clientConfigApiDomain}/rpc/dataextractionloads`;

  const { taskId } = useParams();

  const history = useHistory();
  const { user } = useAuthState();
  const toast = useMessageToast();

  const [
    {
      data: dataExtractionLoadData,
      hasLoaded: dataExtractionLoadDataHasLoaded,
      hasError: dataExtractionLoadDataHasError
    },
    dataExtractionLoadDataFetch
  ] = useFetch();

  const [taskDataKwargs, setTaskDataKwargs] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [hasLoaded, setHasLoaded] = useState(false);

  useEffect(() => {
    setIsLoading(false);
    if (taskId !== undefined) {
      const task_kwargs = taskData.taskData.task_kwargs;
      if (task_kwargs.output_data_location_key_prefix === "NOT_AVAILABLE") {
        task_kwargs.output_data_location_key_prefix = "";
      }
      setTaskDataKwargs(task_kwargs);
    }
    setHasLoaded(true);
  }, [hasLoaded, taskData.taskData.task_kwargs, taskId]);

  useEffect(() => {
    if (taskId === undefined && dataExtractionLoadDataHasLoaded) {
      if (dataExtractionLoadDataHasLoaded) {
        toast.success(`Data Extraction Load submitted successfully.`);

        history.push(`/tasks`);
      } else if (dataExtractionLoadDataHasError) {
        toast.error(`Failed to submit Data Extraction Load.`);
      }
    } else {
      if (dataExtractionLoadDataHasLoaded) {
        toast.success(`Data Extraction Load rerun submitted successfully.`);

        history.push(`/tasks/${dataExtractionLoadData.id}`);
      } else if (dataExtractionLoadDataHasError) {
        toast.error(`Failed to submit rerun of Data Extraction Load.`);
      }
    }
  }, [
    taskId,
    dataExtractionLoadData,
    dataExtractionLoadDataHasLoaded,
    dataExtractionLoadDataHasError,
    toast,
    history
  ]);

  return (
    <Flex.Item span={12}>
      {isLoading ? (
        <Spinner />
      ) : hasLoaded ? (
        <div className={styles.base}>
          <div className={styles.details}>
            <Formik
              initialValues={{
                taskId: taskId,
                clientIdentifier: user.preferences.selectedClient.oid,
                dataBucket: taskDataKwargs.data_bucket,
                outputDataLocationKeyPrefix:
                  taskDataKwargs.output_data_location_key_prefix
              }}
              validationSchema={Yup.object().shape({
                dataBucket: Yup.string().required(
                  "Data Bucket field is required."
                )
              })}
              onSubmit={(values, { setSubmitting, resetForm }) => {
                setSubmitting(true);
                let data = {
                  client_identifier: values.clientIdentifier,
                  data_bucket: values.dataBucket,
                  output_data_location_key_prefix:
                    values.outputDataLocationKeyPrefix,
                  started_by: user.username
                };
                let method = "";
                let url = "";
                method = "post";
                url = `${dataExtractionLoadsRpcUrl}/`;

                if (taskId) {
                  data["rerun_of"] = taskId;
                }

                dataExtractionLoadDataFetch({
                  method,
                  url,
                  data
                });

                setSubmitting(false);
              }}
            >
              {props => {
                const {
                  values,
                  touched,
                  errors,
                  handleChange,
                  handleBlur,
                  handleSubmit
                } = props;

                return (
                  <form onSubmit={handleSubmit} autoComplete="off">
                    <div className={styles.detailsFlex}>
                      <h3>Task Details</h3>
                    </div>
                    <div>
                      <h4>Client OID</h4>
                      <span>{values.clientIdentifier}</span>
                    </div>
                    <div>
                      <TextBox
                        errorText={
                          touched.dataBucket ? errors.dataBucket : null
                        }
                        id="dataBucket"
                        label={
                          <Label
                            error={touched.dataBucket && errors.dataBucket}
                            htmlFor="dataBucket"
                          >
                            <b>Data Bucket</b>
                          </Label>
                        }
                        onChange={handleChange}
                        onBlur={handleBlur}
                        required
                        value={values.dataBucket}
                        width="33%"
                      />
                    </div>
                    <div className={styles.detailsFlex}>
                      <h3>Advanced Options</h3>
                    </div>
                    <div>
                      <TextBox
                        errorText={
                          touched.outputDataLocationKeyPrefix
                            ? errors.outputDataLocationKeyPrefix
                            : null
                        }
                        id="outputDataLocationKeyPrefix"
                        label={
                          <Label
                            error={
                              touched.outputDataLocationKeyPrefix &&
                              errors.outputDataLocationKeyPrefix
                            }
                            htmlFor="outputDataLocationKeyPrefix"
                          >
                            <b>Output Data Location Key Prefix</b>{" "}
                            <Tooltip>
                              <Content maxWidth="30rem">
                                <p>
                                  Only enter this option if you want a folder to
                                  be generated under the resultant output folder
                                  for this task.
                                </p>
                              </Content>
                            </Tooltip>
                          </Label>
                        }
                        onChange={handleChange}
                        onBlur={handleBlur}
                        value={values.outputDataLocationKeyPrefix}
                        width="33%"
                      />
                    </div>
                    <ButtonList>
                      <Button primary type="submit">
                        {taskId ? "Rerun" : "Start"}
                      </Button>
                      <Button
                        onClick={() => {
                          history.push(`/tasks`);
                        }}
                      >
                        Cancel
                      </Button>
                    </ButtonList>
                  </form>
                );
              }}
            </Formik>
          </div>
        </div>
      ) : null}
    </Flex.Item>
  );
};

export default DataExtractionTask;
